import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiHome, FiChevronDown, FiGrid, FiTag, FiFileText } from 'react-icons/fi';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBlogDropdownOpen, setIsBlogDropdownOpen] = useState(false);
  const location = useLocation();
  
  const logo = '/neovam_logo.png';
  const fallbackLogo = 'https://via.placeholder.com/40/38bdf8/ffffff?text=N';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/', icon: FiHome },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' }
  ];

  // Blog dropdown menu items
  const blogMenuItems = [
    { 
      name: 'All Articles', 
      path: '/blog',
      description: 'Browse all our blog posts',
      icon: FiGrid
    },
    { 
      name: 'AI & Machine Learning', 
      path: '/blog/category/Artificial Intelligence',
      description: 'Latest AI trends and insights',
      icon: FiGrid
    },
    { 
      name: 'Cloud Computing', 
      path: '/blog/category/Cloud Computing',
      description: 'Cloud solutions and best practices',
      icon: FiGrid
    },
    { 
      name: 'Fintech Innovations', 
      path: '/blog/category/Fintech',
      description: 'Financial technology updates',
      icon: FiGrid
    },
    { 
      name: 'Digital Transformation', 
      path: '/blog/category/Digital Transformation',
      description: 'Business transformation stories',
      icon: FiGrid
    }
  ];

  // Popular tags for quick access
  const popularTags = [
    { name: 'AI', path: '/blog/tag/AI' },
    { name: 'Startup', path: '/blog/tag/Startup' },
    { name: 'Africa', path: '/blog/tag/Africa' },
    { name: 'Innovation', path: '/blog/tag/Innovation' },
    { name: 'Technology', path: '/blog/tag/Technology' }
  ];

  const isActive = (path) => location.pathname === path;
  const isBlogActive = () => location.pathname.startsWith('/blog');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isBlogDropdownOpen && !event.target.closest('.blog-dropdown')) {
        setIsBlogDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isBlogDropdownOpen]);

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 overflow-visible ${
      isScrolled 
        ? 'bg-white/95 dark:bg-dark-950/95 backdrop-blur-md shadow-lg border-b border-gray-200/20 dark:border-white/10' 
        : 'bg-white/90 dark:bg-dark-950/90 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-0 h-[120px]">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="group flex-shrink-0" aria-label="NeoVam Home">
            <div className="relative h-[68px] w-auto p-1 my-2 sm:my-3 flex items-center justify-center bg-white dark:bg-white rounded-xl">
              <img
                src={logo}
                alt="NeoVam Logo"
                className="h-full w-auto rounded-xl object-contain ring-2 ring-primary-400/20 group-hover:ring-primary-400/50 transition-all duration-300 drop-shadow-lg"
                onError={(e) => {
                  e.currentTarget.src = fallbackLogo;
                }}
              />
              <div className="absolute inset-0 bg-primary-400/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-1 px-2 lg:px-3 py-2 text-sm font-medium transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {item.icon && <item.icon className="w-4 h-4" />}
                {item.name}
                {isActive(item.path) && (
                  <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-600 to-primary-400 dark:from-primary-400 dark:to-primary-600 rounded-full"></div>
                )}
              </Link>
            ))}
            
            {/* Blog Dropdown */}
            <div className="relative blog-dropdown">
              <button
                onClick={() => setIsBlogDropdownOpen(!isBlogDropdownOpen)}
                className={`relative flex items-center gap-1 px-2 lg:px-3 py-2 text-sm font-medium transition-all duration-300 ${
                  isBlogActive()
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <FiFileText className="w-4 h-4" />
                Blog
                <FiChevronDown className={`w-4 h-4 transition-transform duration-300 ${isBlogDropdownOpen ? 'rotate-180' : ''}`} />
                {isBlogActive() && (
                  <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-600 to-primary-400 dark:from-primary-400 dark:to-primary-600 rounded-full"></div>
                )}
              </button>
              
              {/* Dropdown Menu */}
              {isBlogDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white dark:bg-dark-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-dark-600 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 dark:border-dark-700">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2">Blog Categories</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Latest insights from NeoVam</p>
                  </div>
                  
                  <div className="p-4">
                    {blogMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsBlogDropdownOpen(false)}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors mb-2 last:mb-0"
                      >
                        <div className="mt-0.5">
                          <item.icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  
                  <div className="p-4 border-t border-gray-100 dark:border-dark-700 bg-gray-50 dark:bg-dark-900/50">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Popular Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {popularTags.map((tag) => (
                        <Link
                          key={tag.path}
                          to={tag.path}
                          onClick={() => setIsBlogDropdownOpen(false)}
                          className="px-3 py-1.5 bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 rounded-full text-xs font-medium transition-colors"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <ThemeToggle />
            
            <Link
              to="/contact"
              className="ml-2 px-4 lg:px-6 py-2.5 bg-gradient-primary text-white text-sm font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
            >
              Get Started
            </Link>
          </nav>

          {/* Mobile Controls */}
          <div className="md:hidden flex items-center gap-3">
            <ThemeToggle />
            <button
              className="p-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors touch-manipulation"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <FiX size={28} /> : <FiMenu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white/95 dark:bg-dark-950/95 backdrop-blur-md border-t border-gray-200/20 dark:border-white/10 py-4 shadow-xl">
            <nav className="flex flex-col space-y-2 px-4 sm:px-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-4 text-base font-medium rounded-lg transition-all duration-300 touch-manipulation ${
                    isActive(item.path)
                      ? 'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-400/10'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.icon && <item.icon className="w-4 h-4" />}
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Blog Section */}
              <div className="mt-2 mb-2">
                <div className="px-4 py-2">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Blog</h3>
                </div>
                
                {blogMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-3 px-4 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <item.icon className="w-4 h-4" />
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">{item.description}</div>
                    </div>
                  </Link>
                ))}
                
                {/* Popular Tags in Mobile */}
                <div className="px-4 py-3">
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Popular Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {popularTags.map((tag) => (
                      <Link
                        key={tag.path}
                        to={tag.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 rounded-full text-xs font-medium transition-colors"
                      >
                        {tag.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              
              <Link
                to="/contact"
                className="mt-2 px-4 py-4 bg-gradient-primary text-white text-base font-semibold rounded-lg text-center touch-manipulation"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get Started
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;